import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useAuth } from '@/store/auth';

// Aligné sur profileSchema (tenants-service, routes/users/me.controller.ts)
export const LOCALE_LABELS: Record<string, string> = {
  fr: 'Français',
  en: 'English',
  su: 'Soussou',
  ff: 'Pular',
  man: 'Malinké',
};

// Réponse de GET /users/me et PATCH /users/me/profile
interface MeProfile {
  firstName: string;
  lastName: string;
  phone: string | null;
  preferredLocale: string | null;
}

interface Props { onDone: () => void }

/** Édition de son propre profil (nom, téléphone, langue) ; la session est mise à jour avec la réponse. */
export function ProfileEditForm({ onDone }: Props) {
  const { data: me } = useQuery({
    queryKey: ['users-me'],
    queryFn: async () => (await api.get<MeProfile>('/users/me')).data,
  });
  const [form, setForm] = useState<{ firstName: string; lastName: string; phone: string; preferredLocale: string } | null>(null);
  const values = form ?? (me && {
    firstName: me.firstName,
    lastName: me.lastName,
    phone: me.phone ?? '',
    preferredLocale: me.preferredLocale ?? 'fr',
  });

  const save = useMutation({
    mutationFn: async (body: NonNullable<typeof values>) => {
      // Téléphone vide : non envoyé (le schéma API n'accepte pas null)
      const { phone, ...rest } = body;
      return (await api.patch<MeProfile>('/users/me/profile', phone.trim() ? { ...rest, phone } : rest)).data;
    },
    onSuccess: (updated) => {
      // Nom et langue affichés (TopBar, profil) viennent de la session : elle est réécrite (store + stockage)
      const session = useAuth.getState().session;
      if (session) {
        useAuth.getState().setSession({
          ...session,
          user: {
            ...session.user,
            firstName: updated.firstName,
            lastName: updated.lastName,
            preferredLocale: updated.preferredLocale ?? undefined,
          },
        });
      }
      toast.success('Profil mis à jour');
      onDone();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Mise à jour impossible')),
  });

  if (!values) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  const set = (key: keyof typeof values, v: string) => setForm({ ...values, [key]: v });

  return (
    <form className="grid gap-3 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); save.mutate(values); }}>
      <div className="space-y-1">
        <Label htmlFor="profile-firstName">Prénom</Label>
        <Input id="profile-firstName" required maxLength={60} value={values.firstName} onChange={(e) => set('firstName', e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="profile-lastName">Nom</Label>
        <Input id="profile-lastName" required maxLength={60} value={values.lastName} onChange={(e) => set('lastName', e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="profile-phone">Téléphone</Label>
        <Input id="profile-phone" type="tel" value={values.phone} onChange={(e) => set('phone', e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="profile-locale">Langue préférée</Label>
        <select
          id="profile-locale"
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
          value={values.preferredLocale}
          onChange={(e) => set('preferredLocale', e.target.value)}
        >
          {Object.entries(LOCALE_LABELS).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
        </select>
      </div>
      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Enregistrement…' : 'Enregistrer'}</Button>
        <Button type="button" variant="outline" onClick={onDone}>Annuler</Button>
      </div>
    </form>
  );
}
